import {
  combineReducers,
  configureStore,
  PreloadedState,
  Reducer,
} from "@reduxjs/toolkit"
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux"
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist"
import { authReducer, authSlice } from "../features/auth/authSlice"
import { decisionsSlice } from "../features/simulation-decisions/decisionsSlice"
import {
  simulationReducer,
  simulationSlice,
} from "../features/simulation/simulationSlice"
import {
  userAlertsSlice,
  userAlertsSliceReducer,
} from "../features/user-alerts/userAlertsSlice"
import { RESET_STATE_ACTION_TYPE } from "./actions/resetState"

import { meseeknaApi } from "./services/meseeknaApi"
import { collaborationApi } from "./services/collaborationApi"
import { chatbotApi } from "./services/chatbotApi"
import { simulationCreatorApi } from "./services/simulationCreatorApi"
import listenerMiddleware from "../features/simulation/listenerMiddleware"
import {
  userSidebarSlice,
  userSidebarSliceReducer,
} from "../features/sidebar/sidebarSlice"
import {
  recruiterSlice,
  recruiterSliceReducer,
} from "../features/recruiter/recruiterSlice"
import {
  collaborationReducer,
  collaborationSlice,
} from "../features/collaboration/collaborationSlice"

const reducers = {
  [meseeknaApi.reducerPath]: meseeknaApi.reducer,
  [collaborationApi.reducerPath]: collaborationApi.reducer,
  [chatbotApi.reducerPath]: chatbotApi.reducer,
  [simulationCreatorApi.reducerPath]: simulationCreatorApi.reducer,
  [authSlice.name]: authReducer,
  [simulationSlice.name]: simulationReducer,
  [decisionsSlice.name]: decisionsSlice.reducer,
  [userAlertsSlice.name]: userAlertsSliceReducer,
  [userSidebarSlice.name]: userSidebarSliceReducer,
  [recruiterSlice.name]: recruiterSliceReducer,
  [collaborationSlice.name]: collaborationReducer,
}

const combinedReducer = combineReducers<typeof reducers>(reducers)

// Create the root reducer separately so we can extract the RootState type
export const rootReducer: Reducer<RootState> = (state, action) => {
  if (action.type === RESET_STATE_ACTION_TYPE) {
    state = {} as RootState
  }
  return combinedReducer(state, action)
}

// For testing purposes, we can rehydrate the store with a preloaded state
export function setupStore(preloadedState?: PreloadedState<RootState>) {
  return configureStore({
    reducer: combinedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat([
        meseeknaApi.middleware,
        collaborationApi.middleware,
        chatbotApi.middleware,
        simulationCreatorApi.middleware,
      ]),
  })
}

// Actual store used by the application
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat([
        meseeknaApi.middleware,
        collaborationApi.middleware,
        chatbotApi.middleware,
        simulationCreatorApi.middleware,
      ])
      .prepend(listenerMiddleware.middleware),
})

export const persistor = persistStore(store)

export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof combinedReducer>
export const useTypedDispatch = () => useDispatch<AppDispatch>()
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector
